import { useMemo } from 'react';

import { type EmailTemplateFormListingFragment } from '@graphql/__generated__/graphql';
import type { RichEditorValue } from '@modules/_shared/components/email-editor';
import { useDefaultLotBrochureTemplate } from '@modules/_shared/utils/template';
import type { Language, Translate } from '@shared/hooks/locale';

export type ListingDefaultEmailTemplate = {
  subject: RichEditorValue;
  text: RichEditorValue;
};

export type BuildListingDefaultEmailTemplate = (params: {
  listing: EmailTemplateFormListingFragment;
  brochureTemplateId: string | null;
  language: Language;
  t: Translate;
}) => ListingDefaultEmailTemplate;

export const useListingDefaultEmailTemplate = ({
  listing,
  language,
  t,
  skip,
  build,
}: {
  listing: EmailTemplateFormListingFragment | null;
  language: Language;
  t: Translate;
  skip: boolean;
  build: BuildListingDefaultEmailTemplate;
}) => {
  const shouldSkip = skip || listing == null;
  const usesListingBrochureTemplate = listing?.brochure_doc_template_id != null;
  const {
    defaultLotBrochureTemplateId,
    hasLoadedDefaultLotBrochureTemplates,
    loading,
    error,
  } = useDefaultLotBrochureTemplate(shouldSkip || usesListingBrochureTemplate);
  const hasLoadedEmailTemplate =
    !shouldSkip &&
    (usesListingBrochureTemplate || hasLoadedDefaultLotBrochureTemplates);

  const emailTemplate = useMemo(() => {
    if (!hasLoadedEmailTemplate) {
      return null;
    }
    return build({
      listing,
      brochureTemplateId: defaultLotBrochureTemplateId,
      language,
      t,
    });
  }, [
    build,
    defaultLotBrochureTemplateId,
    hasLoadedEmailTemplate,
    language,
    listing,
    t,
  ]);

  return {
    emailTemplate,
    hasLoadedEmailTemplate,
    loading: !shouldSkip && !usesListingBrochureTemplate && loading,
    error: shouldSkip || usesListingBrochureTemplate ? null : error,
  };
};
